import React, { useState, useEffect } from 'react';
import userManagementService, { PendingUser } from '../../services/userManagement';
import UserApprovalCard from './UserApprovalCard';

/**
 * Component that displays a list of users pending approval
 */
const PendingUsersList: React.FC = () => {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);

  // Load pending users on component mount
  useEffect(() => {
    loadPendingUsers();
  }, []);

  // Function to load pending users
  const loadPendingUsers = async (token?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await userManagementService.getPendingUsers(10, token);
      
      if (token) {
        // Append to existing list for pagination
        setPendingUsers(prev => [...prev, ...response.users]);
      } else {
        // Replace list for initial load or refresh
        setPendingUsers(response.users);
      }
      
      setNextToken(response.nextToken);
      setHasMore(!!response.nextToken);
    } catch (err) {
      console.error('Error loading pending users:', err);
      setError('Failed to load pending users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to load more users (pagination)
  const loadMore = () => {
    if (nextToken) {
      loadPendingUsers(nextToken);
    }
  };

  // Function to refresh the list
  const refreshList = () => {
    loadPendingUsers();
  };

  // Function to handle user approval
  const handleApprove = async (userId: string) => {
    try {
      await userManagementService.approveUser(userId);
      // Remove approved user from the list
      setPendingUsers(prev => prev.filter(user => user.UserId !== userId));
    } catch (err) {
      console.error('Error approving user:', err);
      setError('Failed to approve user. Please try again.');
    }
  };

  // Function to handle user rejection
  const handleReject = async (userId: string, reason?: string) => {
    try {
      await userManagementService.rejectUser(userId, reason);
      // Remove rejected user from the list
      setPendingUsers(prev => prev.filter(user => user.UserId !== userId));
    } catch (err) {
      console.error('Error rejecting user:', err);
      setError('Failed to reject user. Please try again.');
    }
  };

  // Render error state
  if (error) {
    return (
      <div className="pending-users-error">
        <p className="error-message">{error}</p>
        <button className="retry-button" onClick={refreshList}>
          Retry
        </button>
      </div>
    );
  }

  // Render loading state on initial load
  if (loading && pendingUsers.length === 0) {
    return (
      <div className="pending-users-loading">
        <p>Loading users pending approval...</p>
      </div>
    );
  }

  // Render empty state
  if (pendingUsers.length === 0) {
    return (
      <div className="pending-users-empty">
        <p>No users are pending approval at this time.</p>
        <button className="refresh-button" onClick={refreshList}>
          Refresh
        </button>
      </div>
    );
  }

  // Render users list
  return (
    <div className="pending-users-list">
      <div className="list-header">
        <h3>Users Pending Approval</h3>
        <button className="refresh-button" onClick={refreshList}>
          Refresh
        </button>
      </div>
      
      <div className="users-grid">
        {pendingUsers.map(user => (
          <UserApprovalCard
            key={user.UserId}
            user={user}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        ))}
      </div>
      
      {loading && pendingUsers.length > 0 && (
        <div className="loading-more">Loading more users...</div>
      )}
      
      {hasMore && !loading && (
        <button className="load-more-button" onClick={loadMore}>
          Load More
        </button>
      )}
    </div>
  );
};

export default PendingUsersList;